import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import React, { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FormController,
  MuiBox,
  MuiDateField,
  MuiGrid,
  MuiLink,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  StatusBadge,
} from "../../../../components";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
  PAGE_URLS,
} from "../../../../helpers/constants";
import {
  buildActionMenuItems,
  checkFilterMatch,
  compareDates,
  convertToUserFormat,
  getFullName,
  getUniqueFilters,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import {
  GenericParticipantSearchResultsProps,
  ProgramGenericAccountSearchParticipantsResultRowValue,
  SearchProgramResultFormKey,
  UniqueGenericParticipantResultFiltersTypes,
} from "../../modelsAndSchemas/models";
import { programSearchSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { ProgramSearchResultWrapper } from "../programSearchResult/ProgramSearchResult.style";
import { usePermissions } from "../../../../hooks/usePermissions";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";

export const GenericParticipantSearchResults: FC<
  GenericParticipantSearchResultsProps
> = ({ rows, isSearch, pageId }) => {
  const navigate = useNavigate();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const {
    fetchLabel,
    fetchComponentTitle,
    fetchValueByKeyOfDropdown,
    commonLabels,
    useFetchLabel,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();

  const getLabel = useFetchLabel(programSearchSiteCoreKeys.resultFormKey);

  const tableRef = React.useRef<{
    resetPagination: (shouldResetRowsPerPage?: boolean) => void;
  }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  // updated siteCore data
  const [allUpdatedRows, setAllUpdatedRows] = useState<
    ProgramGenericAccountSearchParticipantsResultRowValue[]
  >([]);

  const resetPagination = (shouldResetRowsPerPage: boolean = false) => {
    if (tableRef.current) {
      tableRef.current.resetPagination(shouldResetRowsPerPage);
    }
  };

  const [uniqueFilters, setUniqueFilters] =
    useState<UniqueGenericParticipantResultFiltersTypes>(() => ({
      participantStatus: [],
      deliveryOfficeName: [],
      participantEmail: [],
    }));

  const { control, setValue, getValues, reset } = useForm({
    mode: "all",
    defaultValues: {
      [SearchProgramResultFormKey.participantName]: "",
      [SearchProgramResultFormKey.participantEmail]: "",
      [SearchProgramResultFormKey.programEndDate]: "",
      [SearchProgramResultFormKey.participantStatus]: "", //participantStatus
      [SearchProgramResultFormKey.deliveryOffice]: "",
    },
  });

  const filters = useWatch({ control });

  useEffect(() => {
    resetPagination(true);
    reset();
  }, [rows]);

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData = [...rows];

      localData.forEach((data) => {
        data.participantName = getFullName(
          data.participantFirstName,
          data.participantLastName,
          true,
        );
        data.statusKey = data.participantStatus;

        data.participantStatus = fetchValueByKeyOfDropdown(
          programSearchSiteCoreKeys.searchFormKey,
          programSearchSiteCoreKeys.programStatusList,
          data.participantStatus,
        );
      });
      setAllUpdatedRows(localData);
      setFilteredResults([...localData]);

      setUniqueFilters((prevFilters) => ({
        ...prevFilters,
        [SearchProgramResultFormKey.participantStatus]: getUniqueFilters(
          localData,
          "participantStatus",
        ),
        [SearchProgramResultFormKey.deliveryOffice]: getUniqueFilters(
          localData,
          "deliveryOffice",
        ),
        [SearchProgramResultFormKey.participantEmail]: getUniqueFilters(
          localData,
          "participantEmail",
        ),
      }));
    }
  }, [rows, siteCoreLoading]);

  const handleViewAccount = (id: string) => {
    navigate(PAGE_URLS.VIEW_PARTICIPANT, {
      state: { candidateId: id },
    });
  };

  const handleEditParticipant = (id: string) => {
    navigate(PAGE_URLS.EDIT_PARTICIPANT, {
      state: {
        candidateId: id,
      },
    });
  };

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const {
        participantName,
        participantStatus,
        participantEmail,
        programEndDate,
        deliveryOfficeName,
      } = getValues();

      const myData = allUpdatedRows.filter((item) => {
        const participantNameMatch = checkFilterMatch(
          getValueOrBlank(participantName),
          getValueOrBlank(item.participantName),
        );

        const deliveryOfficeMatch = checkFilterMatch(
          getValueOrBlank(deliveryOfficeName),
          getValueOrBlank(item.deliveryOffice),
        );

        const participantEmailMatch = checkFilterMatch(
          getValueOrBlank(participantEmail),
          getValueOrBlank(item.participantEmail),
        );

        const statusMatch =
          isEmpty(participantStatus) ||
          item.participantStatus?.toLowerCase() ===
            participantStatus?.toLowerCase();

        let isProgramEndDateMatch = compareDates(
          getValueOrBlank(programEndDate),
          getValueOrBlank(item?.programEndDate),
          getValueOrBlank(userCountryDateFormat),
        );

        return (
          statusMatch &&
          isProgramEndDateMatch &&
          participantNameMatch &&
          participantEmailMatch &&
          deliveryOfficeMatch
        );
      });
      setFilteredResults([...myData]);
      resetPagination();
    }
  }, [filters]);

  const columns = [
    {
      id: SearchProgramResultFormKey.participantName,
      label: getLabel(programSearchSiteCoreKeys.participantNameLabel),
      isEmptyValue: true,
      isSortVisible: true,
      className: "cell-program-Key-number",
      render: (row: RowProps) =>
        !row.participantName ? null : (
          <MuiLink
            onClick={() => handleViewAccount(row.id)}
            id={`${pageId}-view-participant`}
            data-testid="view-participant-link"
          >
            <MuiBox>
              <MuiEllipseLabel
                labelTypoClassName="Link"
                text={row.participantName}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
    },
    {
      id: SearchProgramResultFormKey.participantEmail,
      label: getLabel(programSearchSiteCoreKeys.emailIdColumnHeader),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SearchProgramResultFormKey.programEndDate,
      label: getLabel(programSearchSiteCoreKeys.programEndDate),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {isNotEmpty(row.programEndDate)
              ? convertToUserFormat(
                  row.programEndDate,
                  userCountryDateFormat,
                )
              : ""}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: SearchProgramResultFormKey.participantStatus,
      label: getLabel(programSearchSiteCoreKeys.participantStatusHeader),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-status",
      render: (row: any) => (
        <StatusBadge status={row.statusKey} label={row.participantStatus} />
      ),
    },
    {
      id: SearchProgramResultFormKey.deliveryOffice,
      label: getLabel(programSearchSiteCoreKeys.deliveryOfficeHeader),
      className: "cell-program-name",
      isSortVisible: true,
    },
    {
      id: "action",
      label: fetchLabel(
        programSearchSiteCoreKeys.resultFormKey,
        programSearchSiteCoreKeys.actionLabel,
      ),
      render: (row: RowProps) => {
        const genericParticipantActions = [
          {
            permissionKey: MODULE_PERMISSIONS.GENERIC_ACCOUNT_VIEW,
            create: () => ({
              onClick: () => handleViewAccount(getValueOrBlank(row.id)),
              dataTestId: `view`,
              label: commonLabels.view,
              ariaLabel: commonLabels.view,
            }),
          },
          {
            permissionKey: MODULE_PERMISSIONS.GENERIC_ACCOUNT_EDIT,
            create: () => ({
              onClick: () => handleEditParticipant(getValueOrBlank(row.id)),
              dataTestId: `edit`,
              label: commonLabels.edit,
              ariaLabel: commonLabels.edit,
            }),
          },
        ];

        const genericParticipantResultMenuItems = buildActionMenuItems(
          row,
          genericParticipantActions,
          hasModulePermission,
        );

        return genericParticipantResultMenuItems ? (
          <MuiOptionMenu menuItems={genericParticipantResultMenuItems} />
        ) : null;
      },
    },
  ];

  const tableHeaderForm = [
    <FormController
      control={control}
      key={SearchProgramResultFormKey.participantName}
      name={SearchProgramResultFormKey.participantName}
    >
      <MuiTextField
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_50}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        id={`${pageId}-${SearchProgramResultFormKey.participantName}`}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchProgramResultFormKey.participantEmail}
      name={SearchProgramResultFormKey.participantEmail}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.participantEmail}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
        id={`${pageId}-${SearchProgramResultFormKey.participantEmail}`}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchProgramResultFormKey.programEndDate}
      name={SearchProgramResultFormKey.programEndDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          format={userCountryDateFormat}
          id={`${pageId}-${SearchProgramResultFormKey.programEndDate}`}
          clearable
          name={SearchProgramResultFormKey.programEndDate}
          value={
            getValues(SearchProgramResultFormKey.programEndDate) !== ""
              ? dayjs(
                  getValues(SearchProgramResultFormKey.programEndDate),
                  userCountryDateFormat,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              SearchProgramResultFormKey.programEndDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.participantStatus}
      key={SearchProgramResultFormKey.participantStatus}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.participantStatus}
        visibleDefaultSelectOption
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${SearchProgramResultFormKey.participantStatus}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,

    <FormController
      control={control}
      name={SearchProgramResultFormKey.deliveryOffice}
      key={SearchProgramResultFormKey.deliveryOffice}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.deliveryOfficeName}
        visibleDefaultSelectOption
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${SearchProgramResultFormKey.deliveryOffice}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
  ];

  return (
    <ProgramSearchResultWrapper>
      <MuiGrid container>
        <MuiGrid size={{ xs: 12 }} className="form-header">
          <MuiTypography variant="h2SemiBold" className="form-header-left">
            {fetchComponentTitle(programSearchSiteCoreKeys.resultFormKey)}
          </MuiTypography>
        </MuiGrid>
        <MuiGrid size={{ xs: 12 }} className="header-right">
          <MuiTable
            ref={tableRef}
            pageId={pageId}
            tableHeaderForm={tableHeaderForm}
            columns={columns}
            tableResults={[...filteredResults]}
            noDataInApi={rows.length === 0 && !isSearch}
            emptyMessage={commonLabels.applyNecessaryFiltersMessage}
            noResultMessage={commonLabels.noResult}
            priorityResults={[]}
            isLastColumnSticky
          />
        </MuiGrid>
      </MuiGrid>
    </ProgramSearchResultWrapper>
  );
};
